import React, { useEffect, useState } from 'react';
import {
  Package, AlertTriangle, Warehouse,
  TrendingDown, Layers, ShoppingCart,
  RotateCcw, ClipboardCheck, ArrowUpDown, LogOut,
  DollarSign, RefreshCw, Box
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { getInventoriDashboard } from '../../api';

const COLORS = ['#11cdef', '#2dce89', '#fb6340', '#f5365c', '#0d7a8a', '#14b8c4', '#0a3845', '#073845'];

function StatCard({ icon: Icon, label, value, color, sublabel, format }) {
  const displayValue = format === 'currency'
    ? `Rp ${(value || 0).toLocaleString('id-ID', { maximumFractionDigits: 0 })}`
    : (value || 0).toLocaleString();
  return (
    <div className="stat-card">
      <div className="stat-card-body">
        <div className="stat-card-content">
          <h5 className="stat-card-label">{label}</h5>
          <span className="stat-card-value" style={format === 'currency' ? { fontSize: '1.4rem' } : {}}>
            {displayValue}
          </span>
          {sublabel && <span className="stat-card-sublabel">{sublabel}</span>}
        </div>
        <div className="stat-card-icon-col">
          <div className="stat-icon-shape" style={{ background: color + '18', color: color }}>
            <Icon size={24} />
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="tooltip-label">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="tooltip-item" style={{ color: p.color }}>
            {p.name}: <strong>{p.value?.toLocaleString()}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
}

function PageHeader() {
  return (
    <div className="dash-header">
      <div className="dash-header-bg" />
      <div className="dash-header-content">
        <div className="dash-breadcrumb">
          <span>Inventori</span>
          <span className="dash-breadcrumb-sep">/</span>
          <span className="dash-breadcrumb-current">Dashboard Inventori</span>
        </div>
        <h2 className="dash-title">Dashboard Inventori</h2>
        <p className="dash-subtitle">Ringkasan stok, nilai inventaris, dan monitoring stok kritis</p>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, className = '' }) {
  return (
    <div className={`chart-card ${className}`}>
      <div className="chart-card-header">
        <h6 className="chart-card-title">{title}</h6>
        {subtitle && <span className="chart-card-subtitle">{subtitle}</span>}
      </div>
      <div className="chart-card-body">{children}</div>
    </div>
  );
}

const ICON_MAP = {
  'Pembelian': ShoppingCart,
  'Retur': RotateCcw,
  'Opname': ClipboardCheck,
  'Penyesuaian': ArrowUpDown,
  'Stok Keluar': LogOut,
};

const TIPE_COLORS = {
  'Pembelian': '#2dce89',
  'Retur': '#fb6340',
  'Opname': '#11cdef',
  'Penyesuaian': '#14b8c4',
  'Stok Keluar': '#f5365c',
};

export default function DashboardInventori() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadDashboard = () => {
    getInventoriDashboard()
      .then(res => setStats(res.data))
      .catch(err => {
        console.error('Failed to load inventory dashboard', err);
        setError('Gagal memuat data dashboard inventori.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  // Auto-refresh when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadDashboard();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="dash-wrapper">
        <PageHeader />
        <div className="dash-content">
          <div className="dash-loading">
            <div className="spinner-lg" />
            <p>Memuat dashboard inventori...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="dash-wrapper">
        <PageHeader />
        <div className="dash-content">
          <div className="dash-error">
            <AlertTriangle size={32} />
            <p>{error || 'Tidak dapat memuat data.'}</p>
          </div>
        </div>
      </div>
    );
  }

  const kategoriData = stats.stok_per_kategori || [];
  const gudangData = stats.nilai_per_gudang || [];
  const lowStockList = stats.low_stock_list || [];
  const transaksi = stats.transaksi_terbaru || [];
  const stokStatus = stats.stok_status || [];

  const warningCount = stats.out_of_stock_items + stats.low_stock_items;

  const maxKategoriStok = Math.max(...kategoriData.map(k => k.stok), 1);

  return (
    <div className="dash-wrapper">
      <PageHeader />

      <div className="dash-content">
        {/* Row 1: Key Stats */}
        <div className="dash-stats-row">
          <div className="dash-stat-col">
            <StatCard
              icon={Package} label="Total Item" value={stats.total_spareparts}
              color="#11cdef" sublabel="Sparepart terdaftar"
            />
          </div>
          <div className="dash-stat-col">
            <StatCard
              icon={Layers} label="Total Stok" value={stats.total_stock}
              color="#2dce89" sublabel="Unit tersedia"
            />
          </div>
          <div className="dash-stat-col">
            <StatCard
              icon={DollarSign} label="Nilai Inventaris" value={stats.total_nilai}
              color="#fb6340" format="currency" sublabel="Berdasar harga"
            />
          </div>
          <div className="dash-stat-col">
            <StatCard
              icon={AlertTriangle} label="Stok Kritis" value={warningCount}
              color={warningCount > 0 ? '#f5365c' : '#2dce89'}
              sublabel={warningCount > 0 ? `${stats.out_of_stock_items} habis, ${stats.low_stock_items} rendah` : 'Semua aman'}
            />
          </div>
        </div>

        {/* Row 2: Charts */}
        <div className="dash-charts-row">
          {/* Stock Status Donut */}
          <div className="dash-chart-col-4">
            <ChartCard title="Status Stok" subtitle="Distribusi ketersediaan stok">
              {stokStatus.length > 0 ? (
                <div className="donut-wrapper">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={stokStatus}
                        cx="50%" cy="50%"
                        innerRadius={55} outerRadius={85}
                        paddingAngle={3}
                        dataKey="count"
                      >
                        {stokStatus.map((_, idx) => {
                          const colors = ['#f5365c', '#fb6340', '#2dce89'];
                          return <Cell key={idx} fill={colors[idx % colors.length]} />;
                        })}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="donut-legend" style={{ flexDirection: 'column', alignItems: 'center' }}>
                    {stokStatus.map((s, i) => {
                      const colors = ['#f5365c', '#fb6340', '#2dce89'];
                      return (
                        <div key={i} className="donut-legend-item">
                          <span className="donut-dot" style={{ background: colors[i] }} />
                          <span>{s.status}: {s.count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-muted text-sm">Tidak ada data.</p>
              )}
            </ChartCard>
          </div>

          {/* Nilai per Gudang */}
          <div className="dash-chart-col-4">
            <ChartCard title="Nilai per Gudang" subtitle={stats.total_gudang > 0 ? `${stats.total_gudang} gudang aktif` : 'Belum ada gudang'}>
              {gudangData.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={gudangData}
                    layout="vertical"
                    margin={{ top: 5, right: 20, left: 85, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" horizontal={false} />
                    <XAxis type="number" stroke="#8898aa" fontSize={10}
                      tickFormatter={(v) => `Rp${(v / 1000000).toFixed(0)}jt`} />
                    <YAxis type="category" dataKey="gudang" stroke="#8898aa" fontSize={10} width={80} />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload?.length) {
                          const d = payload[0].payload;
                          return (
                            <div className="custom-tooltip">
                              <p className="tooltip-label">{d.gudang}</p>
                              <p style={{ color: '#11cdef', fontSize: 13 }}>
                                Rp {(d.nilai || 0).toLocaleString('id-ID', { maximumFractionDigits: 0 })}
                              </p>
                              <p style={{ color: '#8898aa', fontSize: 12 }}>
                                {d.total} item
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="nilai" radius={[0, 6, 6, 0]} barSize={18}>
                      {gudangData.map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted text-sm">Belum ada data gudang.</p>
              )}
            </ChartCard>
          </div>

          {/* Kategori Distribution */}
          <div className="dash-chart-col-4">
            <ChartCard title="Stok per Kategori" subtitle={stats.kategori_count > 0 ? `${stats.kategori_count} kategori` : ''}>
              {kategoriData.length > 0 ? (
                <div className="region-bar-list">
                  {kategoriData.slice(0, 8).map((k, i) => {
                    const pct = maxKategoriStok > 0 ? (k.stok / maxKategoriStok) * 100 : 0;
                    return (
                      <div key={i} className="region-bar-item" style={{ marginBottom: 6 }}>
                        <div className="region-bar-label">
                          <span className="region-dot" style={{ background: COLORS[i % COLORS.length] }} />
                          <span className="region-name" style={{ fontSize: '0.8rem' }}>{k.kategori}</span>
                        </div>
                        <div className="region-bar-track">
                          <div className="region-bar-fill"
                            style={{
                              width: `${pct}%`,
                              background: `linear-gradient(90deg, ${COLORS[i % COLORS.length]}, ${COLORS[(i + 1) % COLORS.length]})`
                            }}
                          />
                        </div>
                        <span className="region-bar-count" style={{ fontSize: '0.75rem' }}>{k.stok}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted text-sm">Belum ada kategori.</p>
              )}
            </ChartCard>
          </div>
        </div>

        {/* Row 3: Low Stock & Recent Transactions */}
        <div className="dash-charts-row">
          {/* Low Stock List */}
          <div className="dash-chart-col-6">
            <ChartCard
              title="Peringatan Stok Kritis"
              subtitle={lowStockList.length > 0 ? `${lowStockList.length} item perlu perhatian` : 'Semua stok aman'}
            >
              {lowStockList.length > 0 ? (
                <div className="table-wrapper" style={{ boxShadow: 'none', border: '1px solid var(--gray-100)', borderRadius: 8 }}>
                  <table className="data-table" style={{ fontSize: '0.8rem' }}>
                    <thead>
                      <tr>
                        <th>Kode</th>
                        <th>Nama</th>
                        <th>Stok</th>
                        <th>Min</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lowStockList.map(item => (
                        <tr key={item.id}>
                          <td><code style={{ fontSize: '0.7rem' }}>{item.code}</code></td>
                          <td style={{ fontWeight: 500 }}>{item.name}</td>
                          <td style={{
                            fontWeight: 600,
                            color: item.stock === 0 ? '#f5365c' : '#fb6340'
                          }}>
                            {item.stock}
                          </td>
                          <td>{item.min_stok}</td>
                          <td>
                            <span className="badge" style={{
                              background: item.stock === 0 ? '#f5365c18' : '#fb634018',
                              color: item.stock === 0 ? '#f5365c' : '#fb6340',
                              padding: '2px 8px', borderRadius: 4, fontWeight: 600
                            }}>
                              {item.stock === 0 ? 'Habis' : 'Rendah'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 30 }}>
                  <Package size={40} color="#2dce89" style={{ marginBottom: 8 }} />
                  <p className="text-muted">Semua stok dalam kondisi baik</p>
                </div>
              )}
            </ChartCard>
          </div>

          {/* Recent Transactions */}
          <div className="dash-chart-col-6">
            <ChartCard title="Transaksi Terbaru" subtitle="Aktivitas inventori terkini">
              {transaksi.length > 0 ? (
                <div className="table-wrapper" style={{ boxShadow: 'none', border: '1px solid var(--gray-100)', borderRadius: 8 }}>
                  <table className="data-table" style={{ fontSize: '0.8rem' }}>
                    <thead>
                      <tr>
                        <th>Tipe</th>
                        <th>No. Ref</th>
                        <th>Tanggal</th>
                        <th>Keterangan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transaksi.map((tx, idx) => {
                        const Icon = ICON_MAP[tx.tipe] || RefreshCw;
                        const color = TIPE_COLORS[tx.tipe] || '#8898aa';
                        return (
                          <tr key={idx}>
                            <td>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 4, color }}>
                                <Icon size={12} />
                                <span style={{ fontSize: '0.7rem', fontWeight: 600 }}>{tx.tipe}</span>
                              </span>
                            </td>
                            <td><code style={{ fontSize: '0.7rem' }}>{tx.no_ref}</code></td>
                            <td style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{tx.tanggal}</td>
                            <td style={{
                              fontSize: '0.75rem',
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: 'var(--gray-500)'
                            }}>
                              {tx.deskripsi || '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 30 }}>
                  <RefreshCw size={40} color="#8898aa" style={{ marginBottom: 8 }} />
                  <p className="text-muted">Belum ada transaksi</p>
                </div>
              )}
            </ChartCard>
          </div>
        </div>

        {/* Row 4: Additional Info */}
        <div className="dash-charts-row">
          <div className="dash-chart-col-6">
            <ChartCard title="Ringkasan Gudang" subtitle="Informasi gudang dan kapasitas">
              <div className="insurance-stats" style={{ justifyContent: 'flex-start', gap: 24 }}>
                <div className="insurance-stat-item">
                  <Warehouse size={24} color="#11cdef" />
                  <div>
                    <span className="insurance-stat-value">{stats.total_gudang}</span>
                    <span className="insurance-stat-label">Gudang Aktif</span>
                  </div>
                </div>
                <div className="insurance-stat-item">
                  <Box size={24} color="#2dce89" />
                  <div>
                    <span className="insurance-stat-value">{gudangData.length}</span>
                    <span className="insurance-stat-label">Gudang Terisi</span>
                  </div>
                </div>
                <div className="insurance-stat-item">
                  <DollarSign size={24} color="#fb6340" />
                  <div>
                    <span className="insurance-stat-value" style={{ fontSize: '1.1rem' }}>
                      Rp {(stats.total_nilai_beli || 0).toLocaleString('id-ID', { maximumFractionDigits: 0 })}
                    </span>
                    <span className="insurance-stat-label">Nilai Beli Total</span>
                  </div>
                </div>
              </div>
            </ChartCard>
          </div>

          {/* Summary Info */}
          <div className="dash-chart-col-6">
            <ChartCard title="Ringkasan Inventaris" subtitle="Informasi tambahan inventori">
              <div className="dash-charts-row" style={{ margin: 0 }}>
                {[
                  { label: 'Total Item', value: stats.total_spareparts, icon: Package, color: '#11cdef' },
                  { label: 'Kategori', value: stats.kategori_count, icon: Layers, color: '#2dce89' },
                  { label: 'Stok Habis', value: stats.out_of_stock_items, icon: AlertTriangle, color: '#f5365c' },
                  { label: 'Stok Rendah', value: stats.low_stock_items, icon: TrendingDown, color: '#fb6340' },
                ].map((item, idx) => (
                  <div key={idx} style={{ width: '50%', padding: 8, boxSizing: 'border-box' }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 12px', borderRadius: 8,
                      background: 'var(--gray-50)', border: '1px solid var(--gray-100)'
                    }}>
                      <div className="stat-icon-shape" style={{
                        background: item.color + '12', color: item.color,
                        width: 32, height: 32, borderRadius: 8,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <item.icon size={16} />
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{item.label}</div>
                        <div style={{ fontWeight: 700, fontSize: '1rem' }}>{item.value}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ChartCard>
          </div>
        </div>
      </div>
    </div>
  );
}
